import { darken, lighten, type Theme } from '@mui/material/styles';
import { ApprovalStatus, TaxonomyAlignmentType, TaxonomyContributionType } from 'utils/enum';

export const SUCCESS_COLOR = 'success.dark';
export const WARNING_COLOR = 'warning.main';

export function getStatusColor(status: boolean | string | null | undefined): string {
   const sanitizedStatus = typeof status === 'string' ? status.toUpperCase() : status;

   switch (sanitizedStatus) {
      case true:
      case 'APPROVED':
      case 'FINISHED':
      case 'CLOSED':
      case 'FILLED':
      case 'COMPLETED':
      case 'COMPLETE':
      case 'ACTIVE':
      case 'DONE':
      case 'RESOLVED':
      case 'CURRENT':
         return SUCCESS_COLOR;
      case false:
      case 'FAILED':
      case 'DELETED':
      case 'REJECTED':
         return 'error.main';
      case 'ARCHIVED':
      case 'FUTURE':
      case 'ACCEPTED':
      case 'SUPERSEDED':
         return 'blueGrey.400';
      case 'HOLD':
      case 'MITIGATING':
      case 'REVIEW_PENDING':
      case 'REVIEWED':
         return WARNING_COLOR;
      case 'REVIEW':
         return 'orange.600';
      case 'PROGRESS':
      case 'IN_PROGRESS':
      case 'UNFILLED':
      case 'NO_VERIFICATION_NEEDED':
         return 'info.main';
      case 'OPEN':
      case 'NEW':
      case 'EXPIRED':
         return 'grey.600';
      default:
         return 'grey.600';
   }
}

export function getAuditStatusColor(auditStatus: string): string {
   switch (auditStatus) {
      case ApprovalStatus.REVIEWED:
         return 'info.main';
      case ApprovalStatus.APPROVED:
         return SUCCESS_COLOR;
      case ApprovalStatus.ARCHIVED:
         return 'text.disabled';
      case 'UNARCHIVE':
      case 'UNAPPROVE':
      case 'UNREVIEW':
         return WARNING_COLOR;
      case ApprovalStatus.NO_VERIFICATION_NEEDED:
         return SUCCESS_COLOR;
      case ApprovalStatus.IN_PROGRESS:
      default:
         return 'text.primary';
   }
}

export function getContributionTypeColor(contributionTypeCode?: string | null): string {
   switch (contributionTypeCode) {
      case TaxonomyContributionType.SUBSTANTIAL_CONTRIBUTION:
      case TaxonomyContributionType.ENABLING_ACTIVITY:
      case TaxonomyContributionType.TRANSITIONAL_ACTIVITY:
      case TaxonomyContributionType.MINIMUM_SAFEGUARDS:
         return SUCCESS_COLOR;
      case TaxonomyContributionType.NOT_QUALIFIED:
         return 'error.main';
      case 'MIXED':
         return 'success.light';
      case TaxonomyContributionType.DO_NO_SIGNIFICANT_HARM:
      default:
         return 'grey[400]';
   }
}

export function getAlignmentTypeColor(alignmentTypeCode?: string): string {
   switch (alignmentTypeCode) {
      case TaxonomyAlignmentType.ELIGIBLE:
         return 'info.main';
      case TaxonomyAlignmentType.ALIGNED:
         return SUCCESS_COLOR;
      case TaxonomyAlignmentType.NOT_ALIGNED:
         return 'error.main';
      case TaxonomyAlignmentType.NOT_ELIGIBLE:
         return 'grey[400]';
      case TaxonomyAlignmentType.OPEN:
         return 'warning.main';
      case 'null':
      default:
         return 'grey[400]';
   }
}

type Color = `#${string}`;

export function generateColorPalette(startColor: Color, endColor: Color, numberOfColors: number): Color[] {
   function hexToRgb(hex: Color): [number, number, number] {
      const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (!match) throw new Error(`Invalid color format: ${hex}`);
      return [parseInt(match[1], 16), parseInt(match[2], 16), parseInt(match[3], 16)];
   }

   function rgbToHex([r, g, b]: [number, number, number]): Color {
      return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
   }

   function interpolate(start: number, end: number, factor: number): number {
      return Math.round(start + (end - start) * factor);
   }

   const startRgb = hexToRgb(startColor);
   const endRgb = hexToRgb(endColor);

   return Array.from({ length: numberOfColors }, (_, i) => {
      const factor = i / (numberOfColors - 1); // Progress factor between 0 and 1
      const interpolatedRgb: [number, number, number] = [
         interpolate(startRgb[0], endRgb[0], factor),
         interpolate(startRgb[1], endRgb[1], factor),
         interpolate(startRgb[2], endRgb[2], factor),
      ];
      return rgbToHex(interpolatedRgb);
   });
}

export function getDataGridBackgroundColor(color: string, theme: Theme, coefficient: number) {
   return {
      backgroundColor: darken(color, coefficient),
      ...theme.applyStyles('light', {
         backgroundColor: lighten(color, coefficient),
      }),
   };
}

/**
 * Get color for risk priority level
 * Handles both numeric (1, 2, 3) and string ('LOW', 'MEDIUM', 'HIGH') priorities
 */
export function getRiskPriorityColor(priority: number | string): string {
   const numericPriority = typeof priority === 'string' ? priorityStringToNumber(priority) : priority;

   switch (numericPriority) {
      case 3: // HIGH
         return 'error.main';
      case 2: // MEDIUM
         return WARNING_COLOR;
      case 1: // LOW
         return SUCCESS_COLOR;
      default:
         return 'grey.500';
   }
}

/**
 * Get color for risk level in risk matrix
 * Handles: NO_RISK, LOW, MEDIUM, HIGH, CRITICAL
 */
export function getRiskLevelColor(level: string): string {
   switch (level) {
      case 'NO_RISK':
         return SUCCESS_COLOR;
      case 'LOW':
         return 'success.light';
      case 'MEDIUM':
         return WARNING_COLOR;
      case 'HIGH':
         return 'orange.500';
      case 'CRITICAL':
         return 'error.main';
      default:
         return 'grey.300';
   }
}

/**
 * Get color for mitigation status
 * Handles: OPEN, PROGRESS, REVIEW, FINISHED
 */
export function getMitigationStatusColor(status: string): string {
   switch (status) {
      case 'OPEN':
         return 'error.main';
      case 'PROGRESS':
         return WARNING_COLOR;
      case 'REVIEW':
         return 'info.main';
      case 'FINISHED':
         return SUCCESS_COLOR;
      default:
         return 'grey.400';
   }
}

/**
 * Get color for mitigation goal type
 * Handles: REDUCE, TRANSFER, AVOID, ACCEPT
 */
export function getMitigationGoalColor(goal: string): string {
   switch (goal) {
      case 'REDUCE':
         return 'primary.main';
      case 'TRANSFER':
         return 'secondary.main';
      case 'AVOID':
         return 'warning.main';
      case 'ACCEPT':
         return 'info.main';
      default:
         return 'grey.400';
   }
}

/**
 * Get color for assessment status
 * Handles: OVERDUE, DUE_SOON, PENDING, NEVER_ASSESSED, COMPLETED
 */
export function getAssessmentStatusColor(status: string): string {
   switch (status) {
      case 'OVERDUE':
      case 'NEVER_ASSESSED':
         return 'error.main';
      case 'DUE_SOON':
         return WARNING_COLOR;
      case 'PENDING':
         return 'info.main';
      case 'COMPLETED':
         return SUCCESS_COLOR;
      default:
         return 'grey.400';
   }
}

/**
 * Helper function to convert priority string to number
 */
function priorityStringToNumber(priority: string): number {
   switch (priority.toUpperCase()) {
      case 'HIGH':
         return 3;
      case 'MEDIUM':
         return 2;
      case 'LOW':
         return 1;
      default:
         return 0;
   }
}
